import type { OpenAPI } from "simplydo/interfaces";

export type IdeaProjectEventAction =
  | OpenAPI.Schemas["ideaProjectEvents"]["action"]
  | "assessorAssigned"
  | "assessorUnassigned"
  | "assessorInvited"
  | "ideaSubmitted"
  | "ideaUnsubmitted"
  | "labelsAdded"
  | "labelsRemoved"
  | "isPinnedUpdated"
  | "changedSubmissionDeadline"
  | "sentAuthorEmail"
  | "sentAssessmentFeedback"
  | "impactStatementCreated"
  | "impactStatementDeleted";

export type IProjectIdeaEvent = Omit<
  OpenAPI.Schemas["ideaProjectEvents"],
  "action"
> & {
  action: IdeaProjectEventAction;
  previousLane?: string;
  userObject?: OpenAPI.Schemas["User"];
  targetUserObject?: OpenAPI.Schemas["User"];
  targetLaneObject?: OpenAPI.Schemas["ProjectLane"];
  previousLaneObject?: OpenAPI.Schemas["ProjectLane"];
  targetIdea?: OpenAPI.Schemas["Idea"];
  interacted?: string[];
  invitee?: string;
  labels?: string[];
  isPinned?: boolean;
  stage?: string;
  notifyAuthors?: boolean;
};

export const hasEventType = (
  action: IdeaProjectEventAction,
): action is keyof typeof eventTypes => action in eventTypes;

export const getLaneNameFromEvent = (
  event: IProjectIdeaEvent,
  defaultLaneName: string,
  laneKey: "previousLane" | "targetLane" = "previousLane",
) => {
  const laneId =
    laneKey === "previousLane" ? event.previousLane : event.targetLane;
  const laneObject =
    laneKey === "previousLane" ?
      event.previousLaneObject
    : event.targetLaneObject;
  if (!laneId) {
    return defaultLaneName;
  }
  if (laneObject) {
    return laneObject.name;
  }
  return "an unknown lane";
};

export const eventTypes = {
  addedToBoard: { icon: "plus", color: "green" },
  removedFromBoard: { icon: "minus", color: "red" },
  createComment: { icon: "paper plane", color: "blue" },
  changedLane: { icon: "arrow right", color: "blue" },
  assignedUser: { icon: "user plus", color: "green" },
  assignedExternalUser: { icon: "user plus", color: "green" },
  unassignedUser: { icon: "user times", color: "red" },
  changedDeadline: { icon: "calendar", color: "blue" },
  changedReviewDate: { icon: "calendar", color: "blue" },
  ideaAssessorAssigned: { icon: "percent", color: "blue" },
  assessorAssigned: { icon: "user plus", color: "blue" },
  assessorUnassigned: { icon: "user times", color: "red" },
  assessorInvited: { icon: "mail", color: "blue" },
  assessmentSubmitted: { icon: "percent", color: "blue" },
  assessmentHasConflict: { icon: "percent", color: "orange" },
  sentAssessmentFeedback: { icon: "mail", color: "blue" },
  ideaSubmitted: { icon: "upload", color: "green" },
  ideaUnsubmitted: { icon: "undo", color: "orange" },
  labelsAdded: { icon: "tags", color: "blue" },
  labelsRemoved: { icon: "tags", color: "red" },
  isPinnedUpdated: { icon: "pin", color: "blue" },
  changedSubmissionDeadline: { icon: "calendar", color: "blue" },
  sentAuthorEmail: { icon: "mail", color: "blue" },
  impactStatementCreated: { icon: "chart line", color: "green" },
  impactStatementDeleted: { icon: "trash", color: "red" },
};
